import type { LoginResponse, ProblemDetails } from "@wakaskul/shared";

import { useAuthStore } from "./auth-store";

const BASE_URL = import.meta.env.VITE_API_URL ?? "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly problem: ProblemDetails,
    readonly status: number,
  ) {
    super(problem.title);
    this.name = "ApiError";
  }

  /** Per-field messages, in the shape react-hook-form's setError expects. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem.errors ?? {};
  }
}

export class OfflineError extends Error {
  constructor() {
    super("You appear to be offline. Your work is saved and will sync when the connection returns.");
    this.name = "OfflineError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Makes a write safe to replay after a dropped connection. */
  idempotencyKey?: string;
  /** Internal: stops a failed refresh from recursing forever. */
  skipRefresh?: boolean;
}

/**
 * Only one refresh runs at a time. Without this, a page that fires six queries
 * on mount would hit /auth/refresh six times, and rotation would treat five of
 * them as token reuse and kill the session.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        useAuthStore.getState().clear();
        return false;
      }

      const data = (await response.json()) as LoginResponse;
      useAuthStore.getState().setSession(data.accessToken, data.user);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, idempotencyKey, skipRefresh, headers, ...rest } = options;

  const token = useAuthStore.getState().accessToken;

  const requestHeaders = new Headers(headers);
  if (body !== undefined) requestHeaders.set("Content-Type", "application/json");
  if (token) requestHeaders.set("Authorization", `Bearer ${token}`);
  if (idempotencyKey) requestHeaders.set("Idempotency-Key", idempotencyKey);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      headers: requestHeaders,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new OfflineError();
  }

  // The access token expired mid-session. Refresh once and replay.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshSession();
    if (refreshed) return api<T>(path, { ...options, skipRefresh: true });
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const problem: ProblemDetails = isProblem(payload)
      ? payload
      : {
          type: "about:blank",
          title: "Something went wrong. Please try again.",
          status: response.status,
        };
    throw new ApiError(problem, response.status);
  }

  return payload as T;
}

function isProblem(value: unknown): value is ProblemDetails {
  return typeof value === "object" && value !== null && "title" in value && "status" in value;
}

/** Restores the session on a cold load, using the httpOnly refresh cookie. */
export async function bootstrapSession(): Promise<void> {
  const restored = await refreshSession();
  if (!restored) useAuthStore.getState().setInitialised();
}
